import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { Auth0Service } from '../services/auth0.service';
import { Auth0Res } from '../../pages/auth/interfaces/auth0.interface';

@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private readonly auth0Service = inject(Auth0Service);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.isTokenRequest(req)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => next.handle(this.cloneWithToken(req, token))),
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.auth0Service.getAuth0Token().pipe(
      switchMap(({ access_token }: Auth0Res) => {
        sessionStorage.setItem('authToken', access_token);
        this.isRefreshing = false;
        this.refreshTokenSubject.next(access_token);
        return next.handle(this.cloneWithToken(req, access_token));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        return throwError(() => err);
      }),
    );
  }

  private cloneWithToken(
    req: HttpRequest<unknown>,
    token: string,
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private isTokenRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes('/oauth/token');
  }
}
